import { Button } from '@shared/components/ui/button';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { Location } from '../../models/Location';
import { LocationCardList, LocationsAgGridTable } from '../display';

interface LocationsViewProps {
  locations: Location[];
  error: unknown;
  onLocationEdit?: (location: Location) => void;
  onLocationDelete?: (location: Location) => void;
  onLocationAdjust?: (location: Location) => void;
  onLocationPrint?: (location: Location, size: 'small' | 'large') => void;
  onLocationToggleLock?: (location: Location) => void;
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  isFetching?: boolean; // 🎯 Para controlar opacity mientras se hace fetch
  sorting: any; // 🎯 Tipo flexible para ser compatible con VirtualTable y AG Grid
  onSortingChange: (newSorting: any) => void;
  total: number;
  isLoading: boolean;
  hasActiveFilters: boolean;
  hasSearchTerm: boolean;
  onCreateLocation?: () => void;
  /** Solo vista escritorio (AG Grid): selección múltiple e impresión de etiquetas */
  selectedLocationIds?: Set<number>;
  onSelectionChanged?: (selectedIds: Set<number>) => void;
  onClearLocationSelection?: () => void;
  onPrintSelectedLabels?: () => void;
  hasTeamData?: boolean;
}

const LocationsView: React.FC<LocationsViewProps> = ({
  locations,
  error,
  onLocationEdit,
  onLocationDelete,
  onLocationAdjust,
  onLocationPrint,
  onLocationToggleLock,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching = false,
  sorting,
  onSortingChange,
  total,
  isLoading,
  hasActiveFilters,
  hasSearchTerm,
  onCreateLocation,
  selectedLocationIds,
  onSelectionChanged,
  onClearLocationSelection,
  onPrintSelectedLabels,
  hasTeamData = false,
}) => {
  const { t } = useTranslation('locationsModule');

  // No renderizar si hay error
  if (error) {
    return null;
  }

  const hasNoResults = hasSearchTerm || hasActiveFilters;
  const shouldShowEmptyView = locations.length === 0 && !isLoading;

  // 🎯 Props compartidas para ambas vistas
  const locationViewProps = {
    locations,
    onLocationEdit,
    onLocationDelete,
    onLocationAdjust,
    onLocationPrint,
    onLocationToggleLock,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sorting,
    onSortingChange,
    total,
  };

  return (
    <div className="mb-4">
      {/* 🎯 EmptyView - Se muestra cuando no hay datos */}
      {shouldShowEmptyView && (
        <EmptyView
          type={hasNoResults ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.LOCATIONS}
          customAction={
            hasNoResults || !onCreateLocation ? null : (
              <Button
                onClick={onCreateLocation}
                variant="primary"
                size="default"
                className="transition-colors mx-auto w-full md:w-auto"
                data-testid="empty-state-create-location-button"
              >
                {t('button.create')}
              </Button>
            )
          }
        />
      )}

      {/* 🎯 Vista Mobile - Cards */}
      {isMobile && (
        <div
          className={
            shouldShowEmptyView
              ? 'opacity-0 h-0 overflow-hidden pointer-events-none'
              : `transition-opacity duration-300 ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`
          }
        >
          <LocationCardList {...locationViewProps} showActions={true} />
        </div>
      )}

      {/* 🎯 Vista Desktop - AG Grid (siempre en DOM) */}
      {!isMobile && (
        <div
          className={
            shouldShowEmptyView
              ? 'opacity-0 h-0 overflow-hidden pointer-events-none'
              : `transition-opacity duration-300 ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`
          }
        >
          <LocationsAgGridTable
            {...locationViewProps}
            selectedLocationIds={selectedLocationIds}
            onSelectionChanged={onSelectionChanged}
            onClearSelection={onClearLocationSelection}
            onPrintSelectedLabels={onPrintSelectedLabels}
            hasTeamData={hasTeamData}
          />
        </div>
      )}
    </div>
  );
};

export default LocationsView;
